import React, { useEffect, useRef, useCallback } from "react";
import CarouselV1 from "@adobe/aem-core-components-react-spa/dist/container/carousel/v1/CarouselV1";
import { GlobalCarouselStyles } from "./CustomCarousel.style";
import { CustomCarouselProps } from "./CustomCarousel.types";

const CustomCarousel: React.FC<CustomCarouselProps> = (props) => {
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const updateNavigationButtons = useCallback(() => {
    const previousButton = carouselRef.current?.querySelector(
      ".cmp-carousel__action--previous"
    ) as HTMLButtonElement;
    const nextButton = carouselRef.current?.querySelector(
      ".cmp-carousel__action--next"
    ) as HTMLButtonElement;
    const bullets = carouselRef.current?.querySelectorAll(".cmp-carousel__indicator");

    const activeIndex = Array.from(bullets || []).findIndex((bullet) =>
      bullet.classList.contains("cmp-carousel__indicator--active")
    );

    if (!previousButton || !nextButton || activeIndex === -1 || !bullets) return;

    previousButton.disabled = activeIndex === 0;
    previousButton.setAttribute("aria-disabled", String(activeIndex === 0));

    nextButton.disabled = activeIndex === bullets.length - 1;
    nextButton.setAttribute("aria-disabled", String(activeIndex === bullets.length - 1));
  }, []);

  const handleBulletClick = useCallback(() => {
    setTimeout(updateNavigationButtons, 0);
  }, [updateNavigationButtons]);

  const observeActiveIndicator = useCallback(() => {
    const indicators = carouselRef.current?.querySelector(".cmp-carousel__indicators");
    if (!indicators) return;

    const observer = new MutationObserver(() => {
      updateNavigationButtons();
    });

    observer.observe(indicators, {
      attributes: true,
      subtree: true,
      attributeFilter: ["class"],
    });

    return observer;
  }, [updateNavigationButtons]);

  const handleKeyDown = useCallback((event: KeyboardEvent, index: number) => {
    const bullets = carouselRef.current?.querySelectorAll(".cmp-carousel__indicator");
    if (!bullets) return;

    const bullet = bullets[index] as HTMLElement;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      bullet.click();
      updateNavigationButtons();
    } else if (event.key === "ArrowRight") {
      const nextIndex = (index + 1) % bullets.length;
      (bullets[nextIndex] as HTMLElement).focus();
    } else if (event.key === "ArrowLeft") {
      const prevIndex = (index - 1 + bullets.length) % bullets.length;
      (bullets[prevIndex] as HTMLElement).focus();
    }
  }, [updateNavigationButtons]);

  const updateAccessibility = useCallback(() => {
    const listBullets = carouselRef.current?.querySelector(".cmp-carousel__indicators");
    const slides = carouselRef.current?.querySelectorAll(".cmp-carousel__item");
    const bullets = carouselRef.current?.querySelectorAll(".cmp-carousel__indicator");

    listBullets?.setAttribute("aria-label", "Escolha um slide para exibir");

    bullets?.forEach((bullet, index) => {
      const element = bullet as HTMLElement;
      element.setAttribute("tabindex", "0");
      element.onkeydown = (event: KeyboardEvent) => handleKeyDown(event, index);
      element.setAttribute("aria-label", "Slide " + (index + 1));
    });

    slides?.forEach((slide, index) => {
      const element = slide as HTMLElement;
      element?.setAttribute("aria-label", "Slide " + (index + 1) + " de " + slides.length);
    });
  }, [handleKeyDown]);

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchMove = (event: React.TouchEvent) => {
    touchEndX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;

    const deltaX = touchStartX.current - touchEndX.current;
    const threshold = 50;

    if (Math.abs(deltaX) > threshold) {
      const bullets = carouselRef.current?.querySelectorAll(".cmp-carousel__indicator");
      const activeIndex = Array.from(bullets || []).findIndex((bullet) =>
        bullet.classList.contains("cmp-carousel__indicator--active")
      );

      if (deltaX > 0 && activeIndex < (bullets?.length || 0) - 1) {
        (bullets![activeIndex + 1] as HTMLElement).click();
      } else if (deltaX < 0 && activeIndex > 0) {
        (bullets![activeIndex - 1] as HTMLElement).click();
      }
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  const hideActionTexts = () => {
    const labelBtns = carouselRef.current?.querySelectorAll(".cmp-carousel__action-text");

    labelBtns?.forEach((btn) => {
      (btn as HTMLElement).style.display = "none";
    });
  };

  useEffect(() => {
    updateAccessibility();
    hideActionTexts();
    updateNavigationButtons();

    const bullets = carouselRef.current?.querySelectorAll(".cmp-carousel__indicator");
    const previousButton = carouselRef.current?.querySelector(".cmp-carousel__action--previous");
    const nextButton = carouselRef.current?.querySelector(".cmp-carousel__action--next");

    bullets?.forEach((bullet) => bullet.addEventListener("click", handleBulletClick));
    previousButton?.addEventListener("click", handleBulletClick);
    nextButton?.addEventListener("click", handleBulletClick);

    const observer = observeActiveIndicator();

    return () => {
      bullets?.forEach((bullet) => bullet.removeEventListener("click", handleBulletClick));
      previousButton?.removeEventListener("click", handleBulletClick);
      nextButton?.removeEventListener("click", handleBulletClick);

      observer?.disconnect();
    };
  }, [handleBulletClick, observeActiveIndicator, updateAccessibility, updateNavigationButtons]);

  return (
    <>
      <GlobalCarouselStyles />
      <div
        className="custom-carousel"
        role="group"
        aria-label="Carousel"
        id={props.id || "carouselId"}
        aria-roledescription="carousel"
        ref={carouselRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <CarouselV1 {...props} />
      </div>
    </>
  );
};

export default CustomCarousel;